import { useState, useEffect } from 'react';
import { submitDemoRequest, DemoFormData } from '../../service/DemoService';

interface DemoModalProps {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
}

const BRANDS = ['Mercedes-Benz', 'BMW', 'Audi', 'Volkswagen', 'Other'];

const EMPTY_FORM: DemoFormData = {
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    brands: [],
    goodsInOperators: '',
    preferredTime: '',
};

const DemoModal = ({ isOpen, onOpen, onClose }: DemoModalProps) => {
    const [formData, setFormData] = useState<DemoFormData>(EMPTY_FORM);
    const [loading, setLoading]   = useState(false);
    const [success, setSuccess]   = useState(false);
    const [error, setError]       = useState('');
    const [brandsError, setBrandsError] = useState(false);
    const [showFloating, setShowFloating] = useState(false);

    /* ── Scroll listener: show floating pill after hero ── */
    useEffect(() => {
        const handleScroll = () => {
            const hero = document.getElementById('hero');
            if (hero) setShowFloating(window.scrollY > hero.offsetHeight);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    /* ── Lock body scroll when modal is open ── */
    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    const handleClose = () => {
        onClose();
        setTimeout(() => {
            setSuccess(false);
            setError('');
            setBrandsError(false);
            setFormData(EMPTY_FORM);
        }, 300);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
    };

    const handleBrandToggle = (brand: string) => {
        setFormData(prev => {
            const already = prev.brands.includes(brand);
            return {
                ...prev,
                brands: already
                    ? prev.brands.filter(b => b !== brand)
                    : [...prev.brands, brand],
            };
        });
        setBrandsError(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (formData.brands.length === 0) {
            setBrandsError(true);
            return;
        }
        setLoading(true);
        setError('');
        try {
            await submitDemoRequest(formData);
            setSuccess(true);
        } catch (err) {
            if (err instanceof Error) setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* ── Floating pill (appears when scrolled past hero) ── */}
            {showFloating && !isOpen && (
                <div className="floating-demo-btn">
                    <button
                        onClick={onOpen}
                        className="cta-amber text-white px-6 py-4 rounded-full font-bold shadow-2xl flex items-center gap-2 hover:scale-105 transition transform"
                        aria-label="Book a Demo"
                    >
                        <i className="fas fa-calendar-check text-lg"></i>
                        <span>Book a Demo</span>
                    </button>
                </div>
            )}

            {/* ── Modal overlay ── */}
            <div
                className={`modal-overlay ${isOpen ? 'open' : ''}`}
                onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="demoModalTitle"
            >
                <div className="modal-box">

                    {/* Header */}
                    <div className="flex justify-between items-start mb-6">
                        <div>
                            <h2 id="demoModalTitle" className="text-2xl font-bold text-gray-800">Book a Free Demo</h2>
                            <p className="text-gray-500 text-sm mt-1">A 10-min call — we'll set everything up for you.</p>
                        </div>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition text-xl leading-none mt-1"
                            aria-label="Close"
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    {/* Success state */}
                    {success ? (
                        <div className="text-center py-6">
                            <div className="pop-in inline-block">
                                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                                    <i className="fas fa-check-circle text-green-500 text-4xl"></i>
                                </div>
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">You're booked in!</h3>
                            <p className="text-gray-500 text-sm mb-6">
                                Thanks! We'll be in touch shortly to confirm your demo time. Looking forward to speaking with you.
                            </p>
                            <button
                                onClick={handleClose}
                                className="gradient-bg text-white px-8 py-3 rounded-xl font-semibold hover:opacity-90 transition"
                            >
                                Close
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>

                            {error && (
                                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-xl text-sm">
                                    {error}
                                </div>
                            )}

                            {/* First / Last name */}
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">First Name *</label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        required
                                        placeholder="John"
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm transition"
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Last Name *</label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        required
                                        placeholder="Smith"
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm transition"
                                    />
                                </div>
                            </div>

                            {/* Work email */}
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-medium mb-1">Work Email *</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    placeholder="john@yourcompany.com"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm transition"
                                />
                            </div>

                            {/* Company */}
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-medium mb-1">Company Name *</label>
                                <input
                                    type="text"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleChange}
                                    required
                                    placeholder="Auto Supplies Ltd"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm transition"
                                />
                            </div>

                            {/* Phone */}
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-medium mb-1">Phone Number</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="+44 7700 900000"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm transition"
                                />
                            </div>

                            {/* Brands */}
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-medium mb-2">
                                    Which brands do you represent? *
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {BRANDS.map(brand => (
                                        <label
                                            key={brand}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm cursor-pointer transition select-none ${
                                                formData.brands.includes(brand)
                                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                    : 'border-gray-300 text-gray-700 hover:border-gray-400'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                className="hidden"
                                                checked={formData.brands.includes(brand)}
                                                onChange={() => handleBrandToggle(brand)}
                                            />
                                            {brand}
                                        </label>
                                    ))}
                                </div>
                                {brandsError && (
                                    <p className="text-red-500 text-xs mt-1">Please select at least one brand.</p>
                                )}
                            </div>

                            {/* Goods-in operators */}
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-medium mb-1">Goods-in Operators *</label>
                                <select
                                    name="goodsInOperators"
                                    value={formData.goodsInOperators}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm bg-white transition"
                                >
                                    <option value="">Select…</option>
                                    <option value="1 operator">1 operator</option>
                                    <option value="2 operators">2 operators</option>
                                    <option value="3 operators">3 operators</option>
                                    <option value="4 or more">4 or more</option>
                                </select>
                            </div>

                            {/* Preferred time */}
                            <div className="mb-6">
                                <label className="block text-gray-700 text-sm font-medium mb-1">Preferred Demo Time</label>
                                <select
                                    name="preferredTime"
                                    value={formData.preferredTime}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm bg-white transition"
                                >
                                    <option value="">Any time works</option>
                                    <option value="morning">Morning (9am – 12pm)</option>
                                    <option value="afternoon">Afternoon (12pm – 5pm)</option>
                                    <option value="evening">Evening (5pm – 7pm)</option>
                                </select>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full cta-amber text-white py-3.5 rounded-xl font-bold text-base shadow hover:shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <><i className="fas fa-spinner fa-spin mr-2"></i>Sending…</>
                                ) : (
                                    <><i className="fas fa-calendar-check mr-2"></i>Request My Free Demo</>
                                )}
                            </button>

                            <p className="text-center text-gray-400 text-xs mt-3">
                                <i className="fas fa-lock mr-1"></i> No spam. No commitment. We'll only contact you about your demo.
                            </p>
                        </form>
                    )}
                </div>
            </div>
        </>
    );
};

export default DemoModal;
